import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "../../components/Grid/Grid";
import { CUSTOM_MESSAGES, PAGE_SIZES } from "../../constants/globalConstants";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import MainHeading from "../../components/MainHeading/MainHeading";
import CustomButton from "../../components/CustomButton/CustomButton";
import ThreeDotActionItem from "../../components/ThreeDotActionItem/ThreeDotActionItem";
import useGridHeight from "../../hooks/useGridHeight";
import { useLazyGetModelListQuery } from "../../app/api/modelApi";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useAppSelector } from "../../redux/store";
import { useFilterManager } from "../../hooks/useFilterManager";

const ModelPortal = () => {
  const { handleFilterChange } = useFilterManager();
  const navigate = useNavigate();
  useGridHeight();

  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [brandId, setBrandId] = useState<string | null>(
    persistDropdownSelector["brand_selector"]?.value ?? null
  );
  const [familyId, setFamilyId] = useState<string | null>(
    persistDropdownSelector["family_selector"]?.value ?? null
  );

  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getModelListQuery, modelListResponse] = useLazyGetModelListQuery();

  const pageSizeRef = useRef(String(PAGE_SIZES.Default_List_Size));
  const tableHeaders = [
    {
      headerName: "Name",
      shouldSort: true,
    },
    {
      headerName: "Unique ID",
      shouldSort: true,
    },
    {
      headerName: "Brand Name",
      shouldSort: true,
    },
    {
      headerName: "Family Name",
      shouldSort: true,
    },
    {
      headerName: "Status",
    },
    { headerName: "" },
  ];

  const asyncFormSelectProps = [
    {
      label: "Brand",
      name: "brand_name",
      onChange: (val: string | null, label: string) => {
        if (val !== brandId) {
          setBrandId(val);
          setFamilyId(null);
          handleFilterChange({
            updatedField: "brand_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getBrandListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
      },
      optionLabelName: "brand_name",
      placeholder: "Select Brand",
      shouldFetch: true,
      persistedOption: persistDropdownSelector["brand_selector"]
        ? {
            label: persistDropdownSelector["brand_selector"]?.label,
            value: persistDropdownSelector["brand_selector"]?.value,
          }
        : null,
    },
    {
      label: "Family",
      name: "family_name",
      onChange: (val: string | null, label: string) => {
        setFamilyId(val);
        handleFilterChange({
          updatedField: "family_id",
          persistedSelection: {
            label,
            value: val,
          },
        });
      },
      fetchQuery: getFamilyListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
      },
      optionLabelName: "family_name",
      placeholder: "Select Family",
      key: brandId,
      shouldFetch: brandId?.length,
      persistedOption: persistDropdownSelector["family_selector"]
        ? {
            label: persistDropdownSelector["family_selector"]?.label,
            value: persistDropdownSelector["family_selector"]?.value,
          }
        : null,
    },
  ];

  const handleGetModelList = async () => {
    try {
      const response = await getModelListQuery({
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
      }).unwrap();

      if (response?.status_code === 200) {
        setTableData(response?.data);
      }
    } catch (error) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Fetch Model List Error",
      });
    }
  };

  useEffect(() => {
    if (brandId && familyId) {
      handleGetModelList();
    } else {
      setTableData([]);
    }
    // eslint-disable-next-line
  }, [brandId, familyId]);

  const updatedTableData = (tableData ?? []).map((tdata) => ({
    Name: tdata.model_name,
    "Unique ID": tdata.id,
    "Brand Name": tdata.brand_name,
    "Family Name": tdata?.family_name,
    Status: [
      tdata.is_active
        ? { text: "Active", color: "#12da85" }
        : { text: "Inactive", color: "#bbbbbb" },
    ],
    actionIcon: (
      <ThreeDotActionItem
        actionItemsMenuPositionStyles={{
          right: "15px",
          left: "unset",
        }}
        actionIconSubItemsList={[
          {
            actionItemName: "View Details",
            onClick: (id: string) =>
              navigate(`/provisioning/model/create?id=${tdata.id}`),
          },
          {
            actionItemName: "Edit Details",
            onClick: (id: string) =>
              navigate(`/provisioning/model/edit?id=${tdata.id}`),
          },
        ]}
        id={tdata.id}
      />
    ),
  }));

  const handleLoadMoreClick = async () => {
    try {
      if (
        modelListResponse.isSuccess &&
        modelListResponse.data?.next_start_key
      ) {
        const response = await getModelListQuery(
          {
            startKey: modelListResponse.data?.next_start_key ?? "",
            page_size: pageSizeRef.current,
            brand_id: brandId,
            family_id: familyId,
          },
          true
        );

        if (response && response?.data?.data) {
          setTableData((prev) => [...prev, ...response?.data?.data]);
        }
      }
    } catch (error) {
      console.error(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Load More Model List Error",
      });
    }
  };

  return (
    <>
      <Layout>
        <MainHeading headerText="Model" subHeaderText={"Description"}>
          <CustomButton
            isSubmitting={false}
            label="Create Model"
            customClass="w-11rem px-1 py-0 m-0 h-48 fs-6"
            onClick={() => navigate("create")}
            disabled={false}
          />
        </MainHeading>

        <Grid
          tableHeaders={tableHeaders}
          tableData={updatedTableData}
          isLoading={modelListResponse.isLoading}
          isDataPresent={tableData?.length > 0}
          isError={modelListResponse.isError}
          error={modelListResponse.error}
          noDataUIMainText={CUSTOM_MESSAGES.No_Model_Yet}
          noDataUISubText={CUSTOM_MESSAGES.Create_Model_Now}
          noDataUIButtonLabel="Create Model"
          showPageSizeSelector
          page_size={pageSizeRef.current}
          handlePageSizeChange={(val) => {
            if (val !== pageSizeRef.current) {
              pageSizeRef.current = val as string;
              handleGetModelList();
            }
          }}
          sort={{ sortField: "Name", sortOrder: "asc" }}
          handleClick={() => navigate("create")}
          asyncFormSelectProps={asyncFormSelectProps}
        />

        {((modelListResponse.isSuccess &&
          modelListResponse?.data?.next_start_key) ||
          modelListResponse.isFetching) && (
          <div className="text-center" id="loadmore-button">
            <CustomButton
              isSubmitting={modelListResponse.isFetching}
              label="Load More"
              customClass="px-1 mw-100 py-0 m-0 mt-3 fs-6"
              customStyles={{ width: "30rem" }}
              onClick={handleLoadMoreClick}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default ModelPortal;
